import {
  ArrowRightLeft,
  Bell,
  Handshake,
  Inbox,
  MoreHorizontal,
  SquareCheck,
  User,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { NAV_ROW_ATTR } from "../../layout/shortcuts";
import { formatDateShort } from "../../misc/osloDate";
import {
  formatValue,
  rowReason,
  rowTone,
  signalBreakdown,
  type FocusRow,
  type FocusRowType,
} from "./ranking";

export const ROW_ICON: Record<FocusRowType, LucideIcon> = {
  task: SquareCheck,
  next_step: Handshake,
  opportunity: Handshake,
  lead: Inbox,
  customer: User,
  agreement: ArrowRightLeft,
  drift: Wrench,
  notification: Bell,
};

export type RowMenuItem =
  | { label: string; onSelect: () => void; shortcut?: string }
  | { label: string; items: { label: string; onSelect: () => void }[] }
  | "separator";

// Rød prikk for forfalt og manglende neste steg, gul for resten. Fargen er
// aldri alene: grunnteksten sier det samme (kap. 12).
export const ToneDot = ({ tone }: { tone: "red" | "yellow" }) => (
  <span
    aria-hidden
    className={cn(
      "inline-block size-1.5 shrink-0 rounded-full",
      tone === "red" ? "bg-red-500" : "bg-amber-500",
    )}
  />
);

export const Initials = ({ name }: { name: string }) => (
  <span
    title={name}
    className="flex size-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-medium text-muted-foreground"
  >
    {name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("")}
  </span>
);

/** Datoen eller verdien til høyre. */
export const rowMarker = (row: FocusRow) =>
  row.markerDate
    ? formatDateShort(row.markerDate)
    : row.value && (row.type === "opportunity" || row.type === "lead")
      ? formatValue(row.value, row.monthlyOnly)
      : null;

// Én rad i «Gjør nå» (kap. 5): typeikon, tittel, grunn og grå metadata,
// merket til høyre og «…»-menyen. Valgt rad får en tynn aksentkant og
// handlingsknappene.
export const FocusRowItem = ({
  row,
  selected,
  owner,
  menu,
  actions,
  hoverActions,
  pinnedActions,
  onSelect,
  onActivate,
  onOpen,
  onOpenFull,
  onKeyDown,
  children,
}: {
  row: FocusRow;
  selected: boolean;
  /** Initialene til eieren (Team). */
  owner?: string | null;
  menu: RowMenuItem[];
  /** Knappene på valgt rad (M6). */
  actions?: ReactNode;
  /** Lysere knapper ved hover på en rad som ikke er valgt. */
  hoverActions?: ReactNode;
  /** Vises på alle rader: «Ta denne» (fiks 4). */
  pinnedActions?: ReactNode;
  onSelect: () => void;
  /** Klikk på raden: velg og åpne kundepanelet. */
  onActivate?: () => void;
  /** Enter: kundepanelet. */
  onOpen: () => void;
  /** Shift+Enter: hele posten. */
  onOpenFull?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** Innhold under raden (skjema eller funnliste). */
  children?: ReactNode;
}) => {
  const Icon = ROW_ICON[row.type];
  const reason = rowReason(row);
  const breakdown = signalBreakdown(row);
  const marker = rowMarker(row);
  return (
    <li className="border-b last:border-b-0">
      <div
        {...{ [NAV_ROW_ATTR]: "" }}
        data-row-key={row.key}
        tabIndex={selected ? 0 : -1}
        aria-current={selected ? "true" : undefined}
        aria-label={`${row.title}. ${reason}`}
        onClick={onActivate ?? onSelect}
        onFocus={onSelect}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter") {
            event.preventDefault();
            if (event.shiftKey && onOpenFull) onOpenFull();
            else onOpen();
            return;
          }
          onKeyDown?.(event);
        }}
        className={cn(
          "group relative flex min-h-[58px] cursor-default items-center gap-3 py-[11px] pr-3 pl-4 outline-none transition-colors",
          "hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-inset",
          selected && "bg-muted/50",
        )}
      >
        {selected && (
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 w-0.5 bg-primary"
          />
        )}
        <Icon
          aria-hidden
          className="size-[15px] shrink-0 text-muted-foreground"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-medium" title={row.title}>
            {row.title}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs">
            {row.pinOrder != null && (
              <span className="shrink-0 text-foreground">Festet i dag ·</span>
            )}
            <ToneDot tone={rowTone(row)} />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="min-w-0 truncate text-foreground/80 sm:shrink-0">
                  {reason}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <ul className="space-y-0.5">
                  {breakdown.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
            {row.meta.length > 0 && (
              <span className="hidden min-w-0 truncate text-muted-foreground sm:inline">
                · {row.meta.join(" · ")}
              </span>
            )}
          </div>
        </div>
        {marker && (
          <span className="hidden shrink-0 text-[13px] text-muted-foreground tabular-nums sm:inline">
            {marker}
          </span>
        )}
        {/* På mobil står handlingene i «…»-menyen, så tittelen får plass. */}
        {selected && actions && (
          <div
            className="hidden shrink-0 items-center gap-2 md:flex"
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
        {!selected && hoverActions && (
          <div
            className="hidden shrink-0 items-center gap-2 opacity-70 md:group-hover:flex"
            onClick={(e) => e.stopPropagation()}
          >
            {hoverActions}
          </div>
        )}
        {pinnedActions && (
          <div
            className="flex shrink-0 items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {pinnedActions}
          </div>
        )}
        {owner && (
          <span className="hidden sm:flex">
            <Initials name={owner} />
          </span>
        )}
        <RowMenu row={row} items={menu} />
      </div>
      {children}
    </li>
  );
};

const RowMenu = ({ row, items }: { row: FocusRow; items: RowMenuItem[] }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 shrink-0 text-muted-foreground"
        aria-label={`Flere valg for ${row.title}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <MoreHorizontal className="size-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      {items.map((item, i) =>
        item === "separator" ? (
          <DropdownMenuSeparator key={`sep-${i}`} />
        ) : "items" in item ? (
          <DropdownMenuSub key={item.label}>
            <DropdownMenuSubTrigger>{item.label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {item.items.map((sub) => (
                <DropdownMenuItem key={sub.label} onSelect={sub.onSelect}>
                  {sub.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuItem key={item.label} onSelect={item.onSelect}>
            {item.label}
            {item.shortcut && (
              <span className="ml-auto pl-4 text-xs text-muted-foreground">
                {item.shortcut}
              </span>
            )}
          </DropdownMenuItem>
        ),
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);
